<template>
  <RootSystemGate @access="onAccess">
    <div v-if="!development" class="rc-notice rc-notice--error" role="alert">
      หน้านี้ใช้ได้เฉพาะ development เท่านั้น
    </div>
    <section v-else class="reconcile" :aria-busy="busy">
      <div class="rc-panel rc-overview">
        <div class="rc-toolbar">
          <div>
            <span class="rc-eyebrow">เปรียบเทียบข้อมูล</span>
            <h3>ข้อมูลทดสอบและข้อมูลอ้างอิง</h3>
            <p class="rc-muted">ตรวจสอบผลคำนวณจากข้อมูลสองช่วงเวลา</p>
          </div>
          <div class="rc-toolbar-actions">
            <span class="rc-badge rc-badge--dev">LOCAL DEV</span
            ><button
              class="rc-button rc-button--primary"
              :disabled="busy || running"
              @click="load(1)"
            >
              <span v-if="busy" class="rc-spinner" aria-hidden="true"></span
              >{{ busy ? 'กำลังตรวจสอบ' : 'ตรวจสอบข้อมูลล่าสุด' }}
            </button>
          </div>
        </div>
        <div class="rc-sources">
          <div class="rc-source">
            <span class="rc-source-label"
              ><span class="rc-dot"></span>ข้อมูลทดสอบ</span
            ><code>wel$prod</code
            ><span class="rc-muted"
              >คำนวณล่าสุด
              <strong>{{
                report ? report.summary.localDate || 'ยังไม่มีข้อมูล' : '—'
              }}</strong></span
            >
          </div>
          <span class="rc-source-divider" aria-hidden="true">⇄</span>
          <div class="rc-source">
            <span class="rc-source-label"
              ><span class="rc-dot rc-dot--teal"></span>ข้อมูลอ้างอิง</span
            ><code>wel$prod_reconcile</code
            ><span class="rc-muted"
              >คำนวณล่าสุด
              <strong>{{
                report ? report.summary.targetDate || 'ยังไม่มีข้อมูล' : '—'
              }}</strong></span
            >
          </div>
        </div>
        <div class="rc-notice">
          <span class="rc-notice-icon" aria-hidden="true">i</span>
          <div>
            <strong>ก่อนเริ่มงาน</strong>
            <p>
              ตรวจเฉพาะสัญญา Approved ใน reconcile · หยุด cron, workers
              และการรับชำระอื่นบน dev
              โดยใช้วันที่คำนวณล่าสุดของแต่ละสัญญาเป็นวันอ้างอิง
            </p>
          </div>
        </div>
      </div>
      <div v-if="error" class="rc-notice rc-notice--error" role="alert">
        <span class="rc-notice-icon" aria-hidden="true">!</span>
        <div>
          <strong>ไม่สามารถดำเนินการได้</strong>
          <p>{{ error }}</p>
        </div>
      </div>
      <div v-if="busy && !report" class="rc-panel rc-empty" role="status">
        <span class="rc-spinner"></span
        ><strong>กำลังตรวจสอบข้อมูลทั้งสองฝั่ง</strong>
        <p class="rc-muted">ข้อมูลจำนวนมากอาจใช้เวลาสักครู่</p>
      </div>
      <template v-if="report">
        <div class="rc-stats">
          <div class="rc-stat">
            <span class="rc-stat-label">สัญญาทั้งหมด</span
            ><strong>{{ number(report.summary.loans) }}</strong
            ><span class="rc-muted">สัญญาในข้อมูลอ้างอิง</span>
          </div>
          <div class="rc-stat rc-stat--blue">
            <span class="rc-stat-label">รอคำนวณ</span
            ><strong>{{ number(report.summary.pendingCalculations) }}</strong
            ><span class="rc-muted">สัญญาที่ยังไม่ถึงวันอ้างอิง</span>
          </div>
          <div class="rc-stat rc-stat--amber">
            <span class="rc-stat-label">รับชำระต่างกัน</span
            ><strong>{{
              report.summary.detailsPending ? '—' : number(receiptDifferences)
            }}</strong
            ><span class="rc-muted"
              >Payment
              {{
                report.summary.detailsPending
                  ? '—'
                  : number(report.summary.paymentDifferences)
              }}
              · ค่าใช้จ่าย
              {{
                report.summary.detailsPending
                  ? '—'
                  : number(report.summary.expenseDifferences)
              }}</span
            >
          </div>
          <button
            type="button"
            class="rc-stat rc-stat--rose rc-stat--clickable"
            :disabled="busy || running"
            @click="showDifferences('BLOCKERS')"
          >
            <span class="rc-stat-label">ข้อมูลตั้งต้นที่ยังต่างกัน</span>
            <strong>{{ number(report.summary.blockers) }}</strong>
            <span class="rc-muted">กดดูรายการที่ต้องตรวจสอบ →</span>
          </button>
        </div>
        <div class="rc-panel">
          <div class="rc-section-heading">
            <div>
              <h3>ดำเนินการ</h3>
              <p class="rc-muted">
                นำเข้าข้อมูลก่อน แล้วเลือกประมวลผลแต่ละส่วน
              </p>
            </div>
            <span v-if="running" class="rc-badge rc-badge--blue"
              >กำลังทำงาน</span
            >
          </div>
          <div
            v-if="report.summary.detailsPending"
            class="rc-notice"
            role="status"
          >
            พบสัญญาที่ต้องนำเข้าหรือปรับสถานะ กรุณากดนำเข้าสัญญาก่อน
            ระบบยังไม่ได้ตรวจรายละเอียดรับชำระและผลคำนวณทั้งหมด
          </div>
          <div class="rc-workflow">
            <div class="rc-step">
              <span class="rc-step-number">01</span>
              <h4>นำเข้าสัญญา</h4>
              <p>
                นำเข้าสัญญาที่ขาด หรือแทนข้อมูลตั้งต้นของสัญญา Draft → Approved
                ตาม reconcile โดยเว้นการอนุมัติค่าใช้จ่ายไว้รอประมวลผล
              </p>
              <span class="rc-step-status"
                >รอนำเข้า
                {{ number(report.summary.pendingContracts) }} สัญญา</span
              >
              <button
                class="rc-button rc-button--primary"
                :disabled="busy || running || !report.summary.pendingContracts"
                @click="choose('CONTRACTS')"
              >
                นำเข้าสัญญา
              </button>
            </div>
            <div class="rc-step">
              <span class="rc-step-number">02</span>
              <h4>นำเข้ารายการรับชำระ</h4>
              <p>
                นำเข้ารายการที่ขาดหรือแตกต่างกัน
                โดยข้ามรายการที่ประมวลผลแล้วและแก้ไขไม่ได้
              </p>
              <span class="rc-step-status"
                >ต่างกัน
                {{
                  report.summary.detailsPending
                    ? '—'
                    : number(receiptDifferences)
                }}
                รายการ</span
              ><button
                class="rc-button rc-button--primary"
                :disabled="
                  busy ||
                  running ||
                  !receiptDifferences ||
                  report.summary.pendingContracts > 0
                "
                @click="choose('IMPORT')"
              >
                นำเข้าทั้งหมด
              </button>
            </div>
            <div class="rc-step">
              <span class="rc-step-number">03</span>
              <h4>คำนวณตามวันอ้างอิง</h4>
              <p>
                เรียก engine
                เมื่อข้อมูลรับชำระและเงื่อนไขตั้งต้นพร้อมสำหรับตรวจสอบ
              </p>
              <span class="rc-step-status"
                >รอคำนวณ
                {{ number(report.summary.pendingCalculations) }} สัญญา</span
              ><button
                class="rc-button rc-button--outline"
                :disabled="busy || running || !report.summary.canCalculate"
                @click="choose('CALCULATE')"
              >
                เรียกคำนวณ
              </button>
            </div>
            <div class="rc-step">
              <span class="rc-step-number">04</span>
              <h4>ประมวลผลค่าใช้จ่าย</h4>
              <p>
                อนุมัติ ExpenseDetail แล้วประมวลผล PaymentExpense
                ตามข้อมูลอ้างอิง แยกจากการคำนวณรายวัน
              </p>
              <span class="rc-step-status"
                >รอประมวลผล
                {{
                  report.summary.detailsPending
                    ? '—'
                    : number(report.summary.pendingExpenses)
                }}
                รายการ</span
              ><button
                class="rc-button rc-button--outline"
                :disabled="
                  busy || running || !report.summary.canProcessExpenses
                "
                @click="choose('EXPENSE')"
              >
                ประมวลผลสำหรับ (ค่าใช้จ่าย)
              </button>
            </div>
          </div>
          <p class="rc-footnote">
            รายการที่นำเข้าได้จะบันทึกใน transaction เดียวกัน
            หากยังมีข้อมูลต่างกันหรือเงื่อนไขค้างอยู่
            ปุ่มประมวลผลจะยังไม่เปิดใช้งาน
          </p>
          <vs-popup
            :title="'ยืนยัน' + actionLabel(action)"
            :active.sync="confirmOpen"
          >
            <form class="rc-confirm" @submit.prevent="start">
              <p v-if="error" class="rc-confirm-error" role="alert">
                {{ error }}
              </p>
              <label for="reconcile-confirmation"
                >พิมพ์ <code>{{ confirmationText }}</code> เพื่อยืนยัน</label
              >
              <input
                id="reconcile-confirmation"
                v-model="confirmation"
                :placeholder="confirmationText"
                autocomplete="off"
                spellcheck="false"
                :disabled="busy || running"
              />
              <div class="rc-confirm-controls">
                <button
                  type="submit"
                  class="rc-button rc-button--primary"
                  :disabled="
                    confirmation !== confirmationText || busy || running
                  "
                >
                  {{ busy ? 'กำลังดำเนินการ…' : 'ยืนยัน' }}</button
                ><button
                  type="button"
                  class="rc-button rc-button--text"
                  :disabled="busy || running"
                  @click="confirmOpen = false"
                >
                  ยกเลิก
                </button>
              </div>
            </form>
          </vs-popup>
        </div>
        <div v-if="job" class="rc-panel rc-job" role="status">
          <div class="rc-section-heading">
            <div>
              <span class="rc-eyebrow">สถานะงานล่าสุด</span>
              <h3>{{ actionLabel(job.action) }}</h3>
            </div>
            <span
              class="rc-badge"
              :class="
                job.state === 'FAILED'
                  ? 'rc-badge--rose'
                  : running
                  ? 'rc-badge--blue'
                  : 'rc-badge--teal'
              "
              >{{ stateLabel(job.state) }}</span
            >
          </div>
          <p v-if="job.action === 'CALCULATE' && job.runId" class="rc-muted">
            แหล่งที่มา: คำนวณตามวันอ้างอิง · CalculationLog Run ID:
            <code>{{ job.runId }}</code>
          </p>
          <div class="rc-progress-label">
            <span
              >ประมวลผล {{ number(job.completed) }} จาก
              {{ number(job.total) }} รายการ</span
            ><span v-if="job.currentId" class="rc-mono">{{
              job.currentId
            }}</span>
          </div>
          <progress
            v-if="job.total"
            :value="job.completed"
            :max="job.total"
            aria-label="ความคืบหน้าของงาน"
          ></progress>
          <p v-if="job.message" class="rc-job-message">{{ job.message }}</p>
          <p v-if="running" class="rc-muted">
            การนำเข้าและประมวลผลค่าใช้จ่ายรอ transaction สำเร็จ
            ส่วนการคำนวณบันทึกตาม engine เดิม หากล้มเหลวอาจสำเร็จบางส่วน
          </p>
          <details
            v-if="job.warnings && job.warnings.length"
            class="rc-warnings"
            open
          >
            <summary>
              รายการที่ข้าม / คำเตือน {{ number(job.warnings.length) }} รายการ
            </summary>
            <ul>
              <li
                v-for="warning in job.warnings"
                :key="warning.table + warning.id"
              >
                <strong>{{ warning.table }}</strong>
                <span class="rc-mono">{{ warning.id }}</span>
                <p>{{ warning.reason }}</p>
              </li>
            </ul>
          </details>
        </div>
        <vs-popup title="ข้อมูลที่ใช้เปรียบเทียบ" :active.sync="infoOpen">
          <div class="rc-comparison-help">
            <p>
              <strong>main (wel$prod)</strong> เทียบกับ
              <strong>main_reconcile (wel$prod_reconcile)</strong>
            </p>
            <p>
              ตรวจเฉพาะสัญญา Approved ใน main_reconcile และข้อมูลที่เกี่ยวข้อง
              ทุก model ไม่นับรายการที่มีเฉพาะ main และ status = Disabled
              และยกเว้นรายการที่ต้นทางมี status = Disabled พร้อมแถว ID
              เดียวกันใน main; หากต้นทาง Enabled แต่ main Disabled
              ยังถือว่าต่างกัน แท็บด้านล่างแสดงขอบเขตการตรวจข้อมูล
              ไม่ใช่การตรวจโครงสร้างฐานข้อมูลทั้งหมด
            </p>
            <vs-tabs>
              <vs-tab
                v-for="model in comparisonModels"
                :key="model.name"
                :label="model.name"
              >
                <div class="rc-help-content">
                  <h4>{{ model.name }}</h4>
                  <p>{{ model.note }}</p>
                  <section
                    v-for="group in model.groups"
                    :key="group.label"
                    class="rc-help-group"
                  >
                    <h5>{{ group.label }} · {{ group.fields.length }} ฟิลด์</h5>
                    <div class="rc-help-fields">
                      <code v-for="field in group.fields" :key="field">{{
                        field
                      }}</code>
                    </div>
                  </section>
                </div>
              </vs-tab>
            </vs-tabs>
            <div class="rc-help-footnote">
              <p>
                รายการที่จับคู่ด้วย id หากมีเพียงฝั่งเดียวจะแสดงผลต่าง record;
                ตัวเลขถือว่าเท่ากันเมื่อผลต่างน้อยกว่า 0.0000001 และ null
                กับค่าที่ไม่มีถือว่าเท่ากัน
              </p>
              <p>
                หากยังรอนำเข้าสัญญา ระบบจะตรวจ Contract / Loan ก่อน
                แล้วจึงอ่านรายละเอียด model อื่นหลังนำเข้า
                ไม่ได้ตรวจทุกคอลัมน์หรือเทียบ PaymentRound, Transaction และ
                TransactionEir โดยตรง
              </p>
            </div>
            <button
              type="button"
              class="rc-help-close"
              @click="infoOpen = false"
            >
              ปิด
            </button>
          </div>
        </vs-popup>
        <div ref="results" class="rc-panel rc-results">
          <div class="rc-section-heading">
            <div>
              <h3>
                {{
                  differenceFilter === 'BLOCKERS'
                    ? 'ข้อมูลตั้งต้นที่ยังต่างกัน'
                    : 'ผลเปรียบเทียบ'
                }}
                <button
                  type="button"
                  class="rc-info-button"
                  aria-label="ดูข้อมูลที่ใช้เปรียบเทียบ"
                  title="ข้อมูลที่ใช้เปรียบเทียบ"
                  @click="infoOpen = true"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 11v6M12 7v1" />
                  </svg>
                </button>
              </h3>
              <button
                v-if="differenceFilter === 'BLOCKERS'"
                type="button"
                class="rc-button rc-button--outline"
                :disabled="busy || running"
                @click="showDifferences('ALL')"
              >
                ดูผลต่างทั้งหมด
              </button>
              <p class="rc-muted">
                แสดงเฉพาะข้อมูลที่ตรวจสอบได้ ·
                ผลต่างอาจเกิดจากข้อมูลตั้งต้นต่างกัน
              </p>
            </div>
            <span
              class="rc-badge"
              :class="
                report.summary.matches ? 'rc-badge--teal' : 'rc-badge--amber'
              "
              >{{
                report.summary.matches
                  ? 'ข้อมูลตรงกัน'
                  : number(
                      selectedContract
                        ? selectedContract.differences.length
                        : report.total
                    ) + ' จุดที่ต้องตรวจสอบ'
              }}</span
            >
          </div>
          <div
            v-if="!report.contracts || !report.contracts.length"
            class="rc-empty"
          >
            <span class="rc-empty-symbol" aria-hidden="true">{{
              report.summary.matches ? '✓' : '—'
            }}</span
            ><strong>{{
              report.summary.matches
                ? 'ข้อมูลที่ตรวจสอบตรงกันทั้งหมด'
                : 'ไม่มีรายการในหน้านี้'
            }}</strong>
            <p class="rc-muted">
              {{
                report.summary.matches
                  ? 'ไม่พบผลต่างในขอบเขตข้อมูลที่ตรวจสอบ'
                  : 'เลือกหน้าก่อนหน้าหรือตรวจสอบข้อมูลล่าสุดอีกครั้ง'
              }}
            </p>
          </div>
          <div v-if="selectedContract" class="rc-drill-heading">
            <button
              type="button"
              class="rc-button rc-button--outline rc-button--compact"
              @click="selectedContract = null"
            >
              <span aria-hidden="true">←</span> กลับรายการสัญญา
            </button>
            <div>
              <strong>{{
                selectedContract.contractNo ||
                selectedContract.contractId ||
                'ไม่ระบุสัญญา'
              }}</strong>
              <p>
                {{ selectedContract.contractName || 'ไม่ระบุชื่อสัญญา' }} ·
                {{ number(selectedContract.differences.length) }} จุดที่ต่างกัน
              </p>
              <router-link
                v-if="selectedContract.contractId"
                class="rc-detail-link"
                :to="
                  '/accounting/contract/detail/' + selectedContract.contractId
                "
                >เปิดข้อมูลสัญญา ↗</router-link
              >
            </div>
          </div>
          <div
            v-if="
              !selectedContract && report.contracts && report.contracts.length
            "
            class="rc-table-wrap"
            tabindex="0"
            role="region"
            aria-label="สัญญาที่มีผลต่าง"
          >
            <table class="rc-contract-table">
              <thead>
                <tr>
                  <th scope="col">สัญญา</th>
                  <th scope="col">Model ที่ต่างกัน</th>
                  <th scope="col" class="rc-numeric">จุดที่ต่างกัน</th>
                  <th scope="col">รายละเอียด</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="contract in report.contracts"
                  :key="contract.contractId || 'unassigned'"
                >
                  <td>
                    <strong class="rc-contract">{{
                      contract.contractNo ||
                      contract.contractId ||
                      'ไม่ระบุสัญญา'
                    }}</strong>
                    <span class="rc-cell-secondary">{{
                      contract.contractName || 'ไม่ระบุชื่อสัญญา'
                    }}</span>
                  </td>
                  <td>
                    <div class="rc-model-list">
                      <span
                        v-for="model in contractModels(contract)"
                        :key="model"
                        class="rc-model-chip"
                        >{{ model }}</span
                      >
                    </div>
                  </td>
                  <td class="rc-numeric">
                    <span class="rc-drift-count">{{
                      number(contract.differences.length)
                    }}</span>
                  </td>
                  <td>
                    <button
                      type="button"
                      class="rc-button rc-button--soft rc-button--compact"
                      :aria-label="
                        'ดูผลต่างสัญญา ' +
                        (contract.contractNo ||
                          contract.contractId ||
                          'ไม่ระบุสัญญา')
                      "
                      @click="selectedContract = contract"
                    >
                      ดูผลต่าง <span aria-hidden="true">→</span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-if="selectedContract"
            class="rc-table-wrap"
            tabindex="0"
            role="region"
            aria-label="ตารางผลเปรียบเทียบ เลื่อนแนวนอนเพื่อดูข้อมูลทั้งหมด"
          >
            <table>
              <caption class="rc-sr-only">
                ผลเปรียบเทียบข้อมูล dev กับข้อมูลอ้างอิง
              </caption>
              <thead>
                <tr>
                  <th scope="col">ตาราง / รายการ</th>
                  <th scope="col">ประเภท</th>
                  <th scope="col">ฟิลด์</th>
                  <th scope="col" class="rc-numeric">Dev</th>
                  <th scope="col" class="rc-numeric">อ้างอิง</th>
                  <th scope="col" class="rc-numeric">
                    ผลต่าง <small>Dev − อ้างอิง</small>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in selectedContract.differences"
                  :key="index"
                >
                  <td>
                    <span class="rc-model">{{ row.table }}</span
                    ><span class="rc-cell-secondary rc-mono">{{ row.id }}</span>
                  </td>
                  <td>
                    <span class="rc-badge" :class="kindClass(row.kind)">{{
                      kindLabel(row.kind)
                    }}</span>
                  </td>
                  <td>
                    <code class="rc-field">{{ row.field }}</code>
                  </td>
                  <td class="rc-numeric">{{ display(row.local) }}</td>
                  <td class="rc-numeric">{{ display(row.reference) }}</td>
                  <td class="rc-numeric rc-delta">{{ delta(row) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="!selectedContract" class="rc-pagination">
            <span class="rc-muted"
              >ทั้งหมด {{ number(report.totalContracts) }} สัญญา ·
              {{ number(report.total) }} จุด · หน้า {{ report.page }} /
              {{
                Math.max(1, Math.ceil(report.totalContracts / report.pageSize))
              }}</span
            >
            <div>
              <button
                class="rc-button rc-button--outline"
                :disabled="busy || running || report.page <= 1"
                @click="load(report.page - 1)"
              >
                ก่อนหน้า</button
              ><button
                class="rc-button rc-button--outline"
                :disabled="
                  busy ||
                  running ||
                  report.page * report.pageSize >= report.totalContracts
                "
                @click="load(report.page + 1)"
              >
                ถัดไป
              </button>
            </div>
          </div>
        </div>
      </template>
    </section>
  </RootSystemGate>
</template>

<script>
import gql from 'graphql-tag'
import RootSystemGate from '../RootSystemGate.vue'
import { apolloClient } from '@/setup/apollo-setup'
const QUERY = gql`
  query CalculationReconcile($page: Int, $filter: String) {
    calculationReconcile(page: $page, filter: $filter, groupContracts: true)
  }
`
const JOB = gql`
  query CalculationReconcileJob {
    calculationReconcileJob
  }
`
const RUN = gql`
  mutation RunCalculationReconcile(
    $action: String!
    $confirmation: String!
    $fingerprint: String!
  ) {
    runCalculationReconcile(
      action: $action
      confirmation: $confirmation
      fingerprint: $fingerprint
    )
  }
`
// Reference fields: backend src/services/reconcileComparison.js (update together when comparison rules change).
const comparisonModels = [
  {
    name: 'Payment',
    note: 'จับคู่ด้วย id ในขอบเขต Loan; รายการขาดหรือฟิลด์ต่างกันเป็น RECEIPT เพื่อพิจารณานำเข้ารายการรับชำระ ไม่เปรียบเทียบ Transaction หรือผลจัดสรรยอดในแท็บนี้',
    groups: [
      {
        label: 'รายการรับชำระ',
        fields: [
          'loanId',
          'paymentType',
          'adjustment',
          'receiveDate',
          'paymentDate',
          'paymentAmount',
          'paymentRemark',
          'status',
          'paymentPrinciple',
          'qrPaymentId',
          'importCode',
        ],
      },
    ],
  },
  {
    name: 'PaymentExpense',
    note: 'จับคู่ด้วย id ในขอบเขต Loan; ฟิลด์ต่างกันเป็น RECEIPT ส่วน approvedStatus จาก Draft ใน dev ไป Approved ในอ้างอิงเป็น EXPENSE_PENDING; สถานะต่างกันแบบอื่นเป็น BLOCKED',
    groups: [
      {
        label: 'รายการรับชำระ',
        fields: [
          'loanId',
          'paymentType',
          'adjustment',
          'receiveDate',
          'paymentDate',
          'paymentAmount',
          'paymentRemark',
          'status',
          'qrPaymentId',
          'paymentContractFee',
          'paymentTaxAmount',
          'paymentProcessingFee',
          'paymentCourtOrderExpense',
        ],
      },
      {
        label: 'สถานะรอประมวลผล / ติดเงื่อนไข',
        fields: ['approvedStatus'],
      },
    ],
  },
  {
    name: 'Contract',
    note: 'ใช้ contractStatus = Approved ของ main_reconcile เป็นเกณฑ์เลือกสัญญา แล้วจับคู่ด้วย id; ข้อมูลตั้งต้นต่างกันเป็น INPUT',
    groups: [
      {
        label: 'ข้อมูลตั้งต้น',
        fields: [
          'contractNo',
          'contractLoanDate',
          'contractCloseDate',
          'contractAmount',
          'restructuring',
          'restructuringReferenceId',
          'restructuringType',
          'educationStatus',
          'badDebt',
          'badDebtDate',
          'contractStatus',
          'status',
        ],
      },
    ],
  },
  {
    name: 'Loan',
    note: 'จับคู่ด้วย id ของ Loan ในขอบเขตสัญญา เปรียบเทียบเงื่อนไขเป็น INPUT และยอดผลคำนวณเป็น RESULT; วันเป้าหมายมาจาก lastUpdate ของแต่ละ Loan ฝั่งอ้างอิง หาก dev เลยวันเป้าหมาย หรือ process ฝั่งใดเปิดอยู่ จะเป็น BLOCKED; อ้างอิงไม่มี lastUpdate จะแจ้ง NOT_COMPARABLE',
    groups: [
      {
        label: 'ข้อมูลตั้งต้น',
        fields: [
          'contractId',
          'loanType',
          'interestRate',
          'interestRateEir',
          'interestType',
          'penaltyRate',
          'penaltyType',
          'debtCollectionFeeType',
          'period',
          'paymentDue',
          'convertInterestDate',
          'coDate',
          'restructuringAmountFixed',
          'status',
        ],
      },
      {
        label: 'ผลคำนวณ',
        fields: [
          'convertInterestAmount',
          'paymentRoundAmount',
          'lastUpdate',
          'loanAmount',
          'paymentAmount',
          'principleRemain',
          'balanceRemain',
          'outstandingAmountRemain',
          'outstandingBalanceRemain',
          'outstandingBalanceRestructuringRemain',
          'accruedFeeRemain',
          'accruedPenaltyRemain',
          'accruedInterestRemain',
          'accruedPrincipleRemain',
          'restructuringFeeRemain',
          'restructuringPenaltyRemain',
          'restructuringInterestRemain',
          'restructuringPrincipleRemain',
          'contractFeeRemain',
          'taxAmountRemain',
          'processingFeeRemain',
          'courtOrderExpenseRemain',
          'agingDate',
          'agingNumber',
        ],
      },
      {
        label: 'เงื่อนไขความพร้อม',
        fields: ['process'],
      },
    ],
  },
  {
    name: 'LoanDetail',
    note: 'จับคู่ด้วย id ในขอบเขต Loan; รายการขาดหรือฟิลด์ต่างกันเป็น INPUT',
    groups: [
      {
        label: 'ข้อมูลตั้งต้น',
        fields: [
          'loanId',
          'seq',
          'loanDate',
          'amount',
          'adjustment',
          'loanDetailType',
          'status',
        ],
      },
    ],
  },
  {
    name: 'InterestRateDetail',
    note: 'จับคู่ด้วย id ในขอบเขต Loan; รายการขาดหรือเงื่อนไขอัตราต่างกันเป็น INPUT',
    groups: [
      {
        label: 'ข้อมูลตั้งต้น',
        fields: [
          'loanId',
          'seq',
          'interestDate',
          'interestRate',
          'interestType',
          'penaltyRate',
          'penaltyType',
          'debtCollectionFeeType',
          'status',
        ],
      },
    ],
  },
  {
    name: 'ExpenseDetail',
    note: 'จับคู่ด้วย id ในขอบเขต Loan; ฟิลด์ตั้งต้นต่างกันเป็น INPUT ส่วน approvedStatus จาก Draft ใน dev ไป Approved ในอ้างอิงเป็น EXPENSE_PENDING รอกดประมวลผลค่าใช้จ่าย; สถานะต่างกันแบบอื่นเป็น BLOCKED',
    groups: [
      {
        label: 'ข้อมูลตั้งต้น',
        fields: ['loanId', 'expenseDate', 'expenseType', 'amount', 'status'],
      },
      {
        label: 'สถานะรอประมวลผล / ติดเงื่อนไข',
        fields: ['approvedStatus'],
      },
    ],
  },
  {
    name: 'Restructuring',
    note: 'จับคู่ด้วย id ในขอบเขต Contract; ยอดยกมาและเงื่อนไขปรับโครงสร้างเป็นข้อมูลตั้งต้น (INPUT)',
    groups: [
      {
        label: 'ข้อมูลตั้งต้น',
        fields: [
          'contractId',
          'restructuringDate',
          'restructuringFeeRemain',
          'restructuringPenaltyRemain',
          'restructuringInterestRemain',
          'restructuringPrincipleRemain',
          'accruedFeeRemain',
          'accruedPenaltyRemain',
          'accruedInterestRemain',
          'accruedPrincipleRemain',
          'principleRemain',
          'balanceRemain',
          'outstandingBalanceRemain',
          'status',
        ],
      },
    ],
  },
  {
    name: 'FeeDetail',
    note: 'ไม่จับคู่ด้วย id แต่จัดกลุ่มตาม Loan + วัน feeDate + feeType + adjustment + status แล้วเทียบผลรวม amount; DebtCollectionFee เป็น RESULT ส่วนประเภทอื่นเป็น INPUT',
    groups: [
      {
        label: 'คีย์จัดกลุ่ม',
        fields: [
          'loanId',
          'feeDate (เฉพาะวัน)',
          'feeType',
          'adjustment',
          'status',
        ],
      },
      {
        label: 'ยอดรวมที่เปรียบเทียบ',
        fields: ['amount (ผลรวมต่อกลุ่ม)'],
      },
    ],
  },
]
export default {
  components: { RootSystemGate },
  data: () => ({
    development: process.env.NODE_ENV === 'development',
    allowed: false,
    busy: false,
    report: null,
    selectedContract: null,
    differenceFilter: 'ALL',
    job: null,
    action: '',
    confirmOpen: false,
    infoOpen: false,
    comparisonModels,
    confirmation: '',
    error: '',
    timer: null,
    disposed: false,
  }),
  computed: {
    running() {
      return this.job && this.job.state === 'RUNNING'
    },
    receiptDifferences() {
      return (
        this.report &&
        this.report.summary.paymentDifferences +
          this.report.summary.expenseDifferences
      )
    },
    confirmationText() {
      return {
        CONTRACTS: 'IMPORT_CONTRACTS',
        IMPORT: 'IMPORT',
        CALCULATE: 'RUN_DAILY_CALCULATORS',
        EXPENSE: 'PROCESS_EXPENSES',
      }[this.action]
    },
  },
  beforeDestroy() {
    this.disposed = true
    clearTimeout(this.timer)
  },
  methods: {
    contractModels(contract) {
      return [...new Set(contract.differences.map((row) => row.table))]
    },
    number(value) {
      return Number(value || 0).toLocaleString('th-TH')
    },
    actionLabel(action) {
      return (
        {
          CONTRACTS: 'นำเข้าสัญญา',
          IMPORT: 'นำเข้ารายการรับชำระ',
          CALCULATE: 'คำนวณตามวันอ้างอิง',
          EXPENSE: 'ประมวลผลค่าใช้จ่าย',
        }[action] || action
      )
    },
    stateLabel(state) {
      return (
        {
          RUNNING: 'กำลังทำงาน',
          COMPLETED: 'เสร็จแล้ว',
          COMPLETED_WITH_WARNINGS: 'เสร็จแล้ว · มีคำเตือน',
          FAILED: 'ไม่สำเร็จ / ต้องตรวจสอบ',
        }[state] || state
      )
    },
    kindClass(kind) {
      return (
        {
          INPUT: 'rc-badge--amber',
          RECEIPT: 'rc-badge--amber',
          RESULT: 'rc-badge--blue',
          BLOCKED: 'rc-badge--rose',
          EXPENSE_PENDING: 'rc-badge--blue',
        }[kind] || ''
      )
    },
    onAccess(allowed) {
      this.allowed = allowed
      this.confirmOpen = false
      this.infoOpen = false
      this.report = null
      this.selectedContract = null
      clearTimeout(this.timer)
      if (allowed && this.development) this.load(1)
    },
    choose(action) {
      this.action = action
      this.confirmation = ''
      this.error = ''
      this.confirmOpen = true
    },
    kindLabel(kind) {
      return (
        {
          INPUT: 'ข้อมูลตั้งต้น',
          RECEIPT: 'รับชำระต่างกัน',
          RESULT: 'ผลคำนวณ',
          BLOCKED: 'ต้องตรวจสอบ',
          NOT_COMPARABLE: 'ไม่มีวันที่คำนวณอ้างอิง',
          EXPENSE_PENDING: 'ค่าใช้จ่ายรอประมวลผล',
        }[kind] || kind
      )
    },
    display(value) {
      return value === null ? 'ไม่มีข้อมูล' : String(value)
    },
    delta(row) {
      return typeof row.local === 'number' && typeof row.reference === 'number'
        ? (row.local - row.reference).toFixed(2)
        : '—'
    },
    async showDifferences(filter) {
      if (this.busy || this.running) return
      this.differenceFilter = filter
      await this.load(1)
      await this.$nextTick()
      if (this.$refs.results)
        this.$refs.results.scrollIntoView({ block: 'start' })
    },
    async load(page) {
      if (!this.allowed || !this.development || this.disposed) return
      this.busy = true
      this.error = ''
      try {
        const { data } = await apolloClient.query({
          query: QUERY,
          variables: { page, filter: this.differenceFilter },
          fetchPolicy: 'no-cache',
        })
        if (!this.allowed || this.disposed) return
        this.report = JSON.parse(data.calculationReconcile)
        this.selectedContract = null
        this.job = this.report.job
        if (this.running) this.schedulePoll()
      } catch (error) {
        this.error = error.message
        this.report = null
      } finally {
        this.busy = false
      }
    },
    async start() {
      if (
        !this.confirmOpen ||
        !this.development ||
        this.busy ||
        this.running ||
        !this.allowed ||
        this.confirmation !== this.confirmationText
      )
        return
      this.busy = true
      this.error = ''
      try {
        const { data } = await apolloClient.mutate({
          mutation: RUN,
          variables: {
            action: this.action,
            confirmation: this.confirmation,
            fingerprint: this.report.fingerprint,
          },
        })
        this.job = JSON.parse(data.runCalculationReconcile)
        this.confirmOpen = false
        this.action = ''
        // Check immediately; terminal jobs refresh through the same load() as the toolbar.
        await this.poll()
      } catch (error) {
        this.error = `${error.message} — หากการเชื่อมต่อขาด ให้ตรวจสถานะก่อนส่งซ้ำ`
        this.schedulePoll()
      } finally {
        this.busy = false
      }
    },
    schedulePoll() {
      clearTimeout(this.timer)
      if (!this.disposed && this.allowed)
        this.timer = setTimeout(() => this.poll(), 3000)
    },
    async poll() {
      if (this.disposed || !this.allowed) return
      try {
        const { data } = await apolloClient.query({
          query: JOB,
          fetchPolicy: 'no-cache',
        })
        if (this.disposed || !this.allowed) return
        this.job = JSON.parse(data.calculationReconcileJob)
        if (this.running) this.schedulePoll()
        else {
          const missingJob = !this.job
          clearTimeout(this.timer)
          await this.load(1)
          if (missingJob)
            this.error =
              'ไม่พบสถานะงานใน process นี้ หาก Backend restart หรือมีหลาย process ต้องตรวจข้อมูลจริงก่อนส่งซ้ำ'
        }
      } catch (error) {
        this.error = `${error.message} — ตรวจสถานะก่อนส่งซ้ำ`
        this.schedulePoll()
      }
    },
  },
}
</script>
<style scoped>
.reconcile {
  --rc-ink: #26334d;
  --rc-muted: #64748b;
  --rc-line: #e5eaf2;
  --rc-primary: #5261d8;
  display: grid;
  gap: 22px;
  color: var(--rc-ink);
  font-size: 14px;
  line-height: 1.65;
}
.reconcile h3,
.reconcile h4,
.reconcile p {
  margin: 0;
}
.reconcile h3 {
  color: var(--rc-ink);
  font-size: 19px;
  font-weight: 600;
  line-height: 1.5;
}
.reconcile h4 {
  color: var(--rc-ink);
  font-size: 16px;
  font-weight: 600;
}
.rc-panel {
  background: #fff;
  border: 1px solid var(--rc-line);
  border-radius: 16px;
  padding: 26px;
  box-shadow: 0 4px 20px rgba(28, 46, 78, 0.035);
  min-width: 0;
}
.rc-toolbar,
.rc-section-heading,
.rc-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}
.rc-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}
.rc-eyebrow {
  display: block;
  color: var(--rc-primary);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  margin-bottom: 5px;
}
.rc-muted {
  color: var(--rc-muted);
  font-size: 13px;
}
.rc-toolbar .rc-muted,
.rc-section-heading .rc-muted {
  margin-top: 5px;
}
.rc-sources {
  display: grid;
  grid-template-columns: 1fr 32px 1fr;
  align-items: center;
  gap: 20px;
  margin: 24px 0;
}
.rc-source {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 18px 22px;
  background: #f8faff;
  border: 1px solid var(--rc-line);
  border-radius: 12px;
  min-width: 0;
}
.rc-source code {
  background: none;
  color: var(--rc-ink);
  font-size: 16px;
  padding: 0;
  overflow-wrap: anywhere;
}
.rc-source-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--rc-muted);
  font-size: 12px;
}
.rc-dot {
  width: 7px;
  height: 7px;
  background: var(--rc-primary, #5261d8);
  border-radius: 50%;
}
.rc-dot--teal {
  background: #168572;
}
.rc-source-divider {
  text-align: center;
  color: #94a3b8;
  font-size: 25px;
}
.rc-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 18px;
  border: 1px solid #ece5ce;
  border-radius: 10px;
  background: #fffbf0;
  color: #72551c;
  font-size: 13px;
}
.rc-notice-icon {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 21px;
  height: 21px;
  border: 1px solid currentColor;
  border-radius: 50%;
  font-weight: 600;
}
.rc-notice--error {
  background: #fff4f4;
  border-color: #f4d6d9;
  color: #a93644;
  overflow-wrap: anywhere;
}
.rc-notice p {
  margin-top: 3px;
}
.rc-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}
.rc-stat {
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 22px;
  border: 1px solid var(--rc-line);
  border-radius: 14px;
  background: #fff;
}
.rc-stat-label {
  font-size: 13px;
  color: var(--rc-muted);
}
.rc-stat > strong {
  font-size: 32px;
  font-weight: 600;
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
}
.rc-stat--blue > strong {
  color: #5261d8;
}
.rc-stat--amber > strong {
  color: #a96a19;
}
.rc-stat--rose > strong {
  color: #b34a62;
}
.rc-badge {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 5px 10px;
  background: #f0f3f8;
  color: #526077;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.45;
}
.rc-badge--dev,
.rc-badge--blue {
  background: #eef0ff;
  color: #4c59be;
}
.rc-badge--dev {
  font-size: 10px;
  letter-spacing: 0.08em;
  font-weight: 700;
  white-space: nowrap;
}
.rc-badge--teal {
  background: #eaf7f2;
  color: #18745f;
}
.rc-badge--amber {
  background: #fff4df;
  color: #95621b;
}
.rc-badge--rose {
  background: #fceef0;
  color: #ac3f54;
}
.rc-workflow {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
  margin-top: 22px;
}
.rc-step {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 20px;
  border: 1px solid var(--rc-line);
  border-radius: 12px;
}
.rc-step-number {
  color: #5968c6;
  background: #f0f2ff;
  border-radius: 8px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
}
.rc-step p {
  font-size: 13px;
  color: var(--rc-muted);
  flex: 1;
}
.rc-step-status {
  font-size: 12px;
  font-weight: 500;
  margin-top: 10px;
}
.rc-step .rc-button {
  width: 100%;
  margin-top: 4px;
}
.rc-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 40px;
  padding: 9px 16px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.45;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.rc-button--primary {
  background: var(--rc-primary, #5261d8);
  color: #fff;
}
.rc-button--primary:hover:not(:disabled) {
  background: #4250bd;
}
.rc-button--outline {
  background: #fff;
  color: #4655ba;
  border-color: #dce1f2;
}
.rc-button--outline:hover:not(:disabled) {
  background: #f3f5ff;
  border-color: #aeb8ee;
}
.rc-button--text {
  background: transparent;
  color: var(--rc-muted);
}
.rc-button:disabled {
  background: #f1f3f7;
  border-color: #e7ebf2;
  color: #8893a5;
  cursor: not-allowed;
}
.rc-button:focus-visible,
.rc-confirm input:focus-visible,
.rc-table-wrap:focus-visible,
summary:focus-visible {
  outline: 3px solid #a6b2fa;
  outline-offset: 3px;
}
.reconcile .rc-footnote {
  margin-top: 18px;
  color: var(--rc-muted);
  font-size: 12px;
}
.rc-confirm-error {
  color: #be123c;
}
.rc-confirm {
  --rc-ink: #26334d;
  --rc-muted: #64748b;
  --rc-primary: #5261d8;
  padding: 8px 4px;
  color: var(--rc-ink);
}
.rc-confirm label {
  display: block;
  margin-bottom: 12px;
  font-size: 14px;
}
.rc-confirm p {
  margin-top: 5px;
  font-size: 13px;
}
.rc-confirm code {
  color: #4858c4;
  background: #eceffd;
  border-radius: 4px;
}
.rc-confirm-controls {
  display: flex;
  gap: 10px;
  margin-top: 20px;
  justify-content: flex-end;
  flex-wrap: wrap;
}
.rc-confirm input {
  flex: 1;
  min-width: 180px;
  max-width: 100%;
  width: 100%;
  background: #fff;
  border: 1px solid #cdd5ed;
  border-radius: 8px;
  padding: 10px 12px;
  color: var(--rc-ink);
  font-family: monospace;
  font-size: 13px;
}
.rc-progress-label {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 13px;
  margin-top: 20px;
}
.rc-job progress {
  display: block;
  width: 100%;
  height: 7px;
  margin: 12px 0 18px;
  border: 0;
  border-radius: 6px;
  overflow: hidden;
  accent-color: var(--rc-primary);
  background: #edf0f8;
}
.rc-job progress::-webkit-progress-bar {
  background: #edf0f8;
}
.rc-job progress::-webkit-progress-value {
  background: var(--rc-primary, #5261d8);
}
.rc-job progress::-moz-progress-bar {
  background: var(--rc-primary, #5261d8);
}
.reconcile .rc-job-message {
  margin: 10px 0;
}
.rc-warnings {
  margin-top: 16px;
  padding: 14px 18px;
  background: #fffbf2;
  border: 1px solid #f0e5cb;
  border-radius: 10px;
}
.rc-warnings summary {
  cursor: pointer;
  color: #87601f;
  font-weight: 500;
}
.rc-warnings ul {
  margin: 12px 0 0;
  padding-left: 18px;
  max-height: 260px;
  overflow: auto;
}
.rc-warnings li {
  padding: 8px 0;
  font-size: 12px;
}
.rc-results {
  padding: 0;
  overflow: hidden;
}
.rc-results > .rc-section-heading {
  padding: 24px 26px;
}
.rc-table-wrap {
  overflow: auto;
  max-height: 620px;
}
.rc-results table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}
.rc-results th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f7f9fc;
  color: #69768d;
  font-weight: 500;
  font-size: 12px;
  text-align: left;
  padding: 14px 18px;
  border-top: 1px solid var(--rc-line);
  border-bottom: 1px solid var(--rc-line);
  white-space: nowrap;
}
.rc-results th small {
  display: block;
  font-weight: 400;
  color: #7a879c;
}
.rc-results td {
  padding: 17px 18px;
  border-bottom: 1px solid #edf0f6;
  vertical-align: top;
  min-width: 110px;
  max-width: 280px;
  overflow-wrap: anywhere;
}
.rc-results tbody tr:hover {
  background: #fafbff;
}
.rc-results tbody tr:last-child td {
  border-bottom: 0;
}
.rc-contract {
  display: block;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}
.rc-cell-secondary {
  display: block;
  margin-top: 5px;
  color: var(--rc-muted);
  font-size: 12px;
}
.rc-model {
  font-weight: 500;
}
.rc-mono {
  font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
  font-size: 11px;
  overflow-wrap: anywhere;
}
.rc-field {
  background: #f5f7fb;
  color: #526078;
  font-size: 11px;
  border-radius: 4px;
  padding: 3px 5px;
  overflow-wrap: anywhere;
}
.rc-results .rc-numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.rc-delta {
  color: #ac4256;
  font-weight: 500;
}
.rc-pagination {
  padding: 18px 24px;
  border-top: 1px solid var(--rc-line);
}
.rc-pagination > div {
  display: flex;
  gap: 8px;
}
.rc-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 22px;
  text-align: center;
}
.rc-empty-symbol {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  color: #18745f;
  border-radius: 50%;
  background: #eaf7f2;
  font-size: 23px;
}
.rc-spinner {
  display: inline-block;
  width: 17px;
  height: 17px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: rc-spin 0.8s linear infinite;
}
.rc-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
@keyframes rc-spin {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .rc-spinner {
    animation: none;
  }
}
@media (max-width: 1100px) {
  .rc-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .rc-workflow {
    grid-template-columns: 1fr;
  }
  .rc-step {
    display: grid;
    grid-template-columns: 38px 1fr;
    column-gap: 14px;
  }
  .rc-step-number {
    grid-row: span 4;
    text-align: center;
    align-self: start;
    padding: 6px;
  }
  .rc-step .rc-button {
    width: fit-content;
  }
}
@media (max-width: 640px) {
  .rc-panel {
    padding: 18px;
  }
  .rc-toolbar,
  .rc-section-heading,
  .rc-pagination {
    flex-direction: column;
    align-items: flex-start;
  }
  .rc-toolbar-actions {
    width: 100%;
    justify-content: space-between;
    gap: 8px;
  }
  .rc-sources {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  .rc-source-divider {
    display: none;
  }
  .rc-stat {
    padding: 16px;
  }
  .rc-stat > strong {
    font-size: 26px;
  }
  .rc-stats {
    gap: 10px;
  }
  .rc-results {
    padding: 0;
  }
  .rc-results > .rc-section-heading {
    padding: 20px;
  }
  .rc-confirm-controls {
    flex-direction: column;
  }
  .rc-confirm input {
    max-width: none;
    min-width: 0;
    width: 100%;
  }
  .rc-pagination {
    padding: 18px;
  }
}
</style>

<style scoped>
.rc-drill-heading {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  margin: 0;
  padding: 18px 26px;
  border-top: 1px solid #e5eaf2;
  background: #f8faff;
}
.rc-results .rc-section-heading h3 {
  display: flex;
  align-items: center;
  gap: 6px;
}
.rc-button--compact {
  min-height: 34px;
  padding: 7px 12px;
  white-space: nowrap;
  flex-shrink: 0;
}
.rc-button--soft {
  background: #eef0ff;
  color: #4655ba;
  border-color: #e2e6ff;
}
.rc-button--soft:hover {
  background: #e0e5ff;
}
.rc-model-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.rc-model-chip {
  background: #f1f5f9;
  color: #526077;
  padding: 3px 8px;
  border-radius: 5px;
  font-size: 12px;
}
.rc-drift-count {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #95621b;
}
.rc-results .rc-contract-table td {
  vertical-align: middle;
}
.rc-results .rc-contract-table th:last-child,
.rc-results .rc-contract-table td:last-child {
  text-align: right;
  width: 140px;
}
.rc-contract-table .rc-contract {
  white-space: normal;
  overflow-wrap: anywhere;
}
.rc-drill-heading > div {
  min-width: 0;
}
.rc-drill-heading strong {
  font-size: 15px;
  overflow-wrap: anywhere;
}
.rc-drill-heading p {
  color: #64748b;
  font-size: 13px;
  margin-top: 4px;
}
.rc-detail-link {
  display: inline-block;
  margin-top: 6px;
  font-size: 12px;
  color: #4655ba;
}
.rc-info-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-left: 6px;
  border: 0;
  border-radius: 50%;
  color: #5261d8;
  background: #eef0ff;
  cursor: pointer;
  vertical-align: middle;
  font-size: 17px;
}
.rc-info-button:hover {
  background: #dfe4ff;
}
.rc-info-button:focus-visible,
.rc-help-close:focus-visible {
  outline: 2px solid #5261d8;
  outline-offset: 3px;
}
.rc-comparison-help {
  color: #26334d;
  line-height: 1.7;
}
.rc-comparison-help p {
  margin: 0 0 12px;
}
.rc-help-content {
  max-height: 48vh;
  overflow-y: auto;
  padding: 16px 4px;
}
.rc-help-content h4 {
  margin-bottom: 10px;
  color: #26334d;
}
.rc-help-group {
  margin-top: 16px;
}
.rc-help-group h5 {
  color: #64748b;
  margin-bottom: 10px;
}
.rc-help-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.rc-help-fields code {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 4px 8px;
  color: #334155;
  overflow-wrap: anywhere;
  max-width: 100%;
}
.rc-help-footnote {
  border-top: 1px solid #e2e8f0;
  padding-top: 14px;
  font-size: 12px;
  color: #64748b;
}
.rc-help-close {
  display: block;
  margin-left: auto;
  border: 0;
  border-radius: 8px;
  padding: 8px 24px;
  color: white;
  background: #5261d8;
  cursor: pointer;
}
</style>
